'use client';

import Popup from '@/components/Popup';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { useStore } from '@/providers/app-store-provider';
import { IconButton } from '@mui/material';
import { CloseOutlined } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import { usePathname } from 'next/navigation';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface RouterInfo {
  refer_page_name: string;
  refer_url_path: string;
}

interface InstadeskPopProps {
  show: boolean;
  instadeskUrl: string;
  // sessionStartUrl?: string; // 没用
  onClose: () => void;
}

// 独立的Iframe组件，使用memo防止重渲染
interface InstadeskIframeProps {
  url: string;
  loading: boolean;
  width?: string;
  height?: string | number;
  className?: string;
  title?: string;
}

// 抽出来。避免多次渲染。
const InstadeskIframe = memo(
  ({
    url,
    loading,
    width = '100%',
    height,
    className = 'border-0',
    title = 'Instadesk Customer Service',
  }: InstadeskIframeProps) => {
    if (loading) {
      return (
        <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <span className="text-gray-600">Loading...</span>
          </div>
        </div>
      );
    }

    if (!url) {
      return null;
    }

    return (
      <iframe
        src={url}
        width={width}
        height={height}
        className={className}
        title={title}
        loading="lazy"
      />
    );
  },
);

export default function InstadeskPop({ show, instadeskUrl, onClose }: InstadeskPopProps) {
  const t = useTranslations('layout.rightBottomFloating');
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionUrl, setSessionUrl] = useState(''); // 访问着陆页
  const [routeEnterTime, setRouteEnterTime] = useState(0);
  const [fromRouterInfo, setFromRouterInfo] = useState<RouterInfo>({
    refer_page_name: '',
    refer_url_path: '',
  });
  const [stayTime, setStayTime] = useState(0);

  // 获取用户信息 - 使用独立的选择器避免不必要的重渲染
  const token = useStore((state) => state.token);
  const userInfo = useStore((state) => state.userInfo);
  const isMobile = useStore((state) => state.isMobileScreen);

  const pathname = usePathname();
  const pcPopupRef = useRef<HTMLDivElement>(null);

  // 监听路由变化，记录页面进入时间
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setRouteEnterTime(new Date().getTime());
      setStayTime(0);
    }
  }, [pathname]);

  // 初始化会话URL - 只在组件挂载时执行一次
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setSessionUrl(window.location.href);
      const { referrer } = document;
      const navigationEntry = performance.getEntriesByType(
        'navigation',
      )[0] as PerformanceNavigationTiming;
      const isNavigateType = navigationEntry?.type === 'navigate';

      setFromRouterInfo({
        refer_page_name: '', // 在 React 中需要不同的方式获取页面名称
        refer_url_path: isNavigateType ? referrer : '',
      });
    }
  }, []);

  // 使用 useMemo 来记忆化 URL 构建，避免不必要的重新计算
  const builtUrl = useMemo(() => {
    if (!instadeskUrl || !show) return '';

    let inUrl = instadeskUrl;

    // 获取来源信息
    const sourceUrl = fromRouterInfo.refer_url_path || '';
    const currentSessionUrl = sessionUrl || '';
    const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

    // 使用稳定的停留时间 - 只有在显示弹窗时才计算一次
    const currentStayTime = stayTime || 0;

    // 构建追踪信息
    const dezhuTrackInfo = {
      needPageTrace: true,
      utmSourceName: '',
      utmTerm: '',
      sourceUrl, // 来源页url
      prevUrl: currentSessionUrl, // 受访页url
      sessionUrl: currentSessionUrl, // 着陆页url
      currentUrl, // 对话页url
      stayTime: currentStayTime, // 停留时间（秒）
    };

    // 添加浏览信息参数
    inUrl = `${inUrl}&layout=false&dezhuTrackInfo=${encodeURIComponent(JSON.stringify(dezhuTrackInfo))}`;

    // 添加用户信息
    const { email, name } = userInfo || {};
    if (token && email && name) {
      inUrl = `${inUrl}&email=${encodeURIComponent(email)}&name=${encodeURIComponent(name)}`;
    }

    return inUrl;
  }, [
    instadeskUrl,
    show,
    fromRouterInfo.refer_url_path,
    sessionUrl,
    stayTime,
    token,
    userInfo?.email,
    userInfo?.name,
  ]);

  // 计算并设置停留时间 - 只在弹窗显示时执行一次
  useEffect(() => {
    if (show && !stayTime && routeEnterTime) {
      const calculatedStayTime = Math.floor((new Date().getTime() - routeEnterTime) / 1000);
      setStayTime(calculatedStayTime);
    }
  }, [show, stayTime, routeEnterTime]);

  // 当弹窗显示且URL构建完成时更新URL
  useEffect(() => {
    if (show && builtUrl && builtUrl !== url) {
      setLoading(true);

      // 延迟加载防止弹窗抖动
      const timer = setTimeout(() => {
        setUrl(builtUrl);
        setLoading(false);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [show, builtUrl]);

  // 防止滚动穿透的逻辑
  useEffect(() => {
    if (show && !isMobile) {
      // 弹窗显示时阻止body滚动
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      // 简单的鼠标滚轮事件阻止（针对PC端）
      const handleWheel = (e: WheelEvent) => {
        const target = e.target as Element;
        // 如果滚动发生在弹窗容器内，阻止冒泡
        if (pcPopupRef.current?.contains(target)) {
          e.stopPropagation();
        }
      };

      document.addEventListener('wheel', handleWheel, { passive: true });

      return () => {
        // 弹窗关闭时恢复body滚动
        document.body.style.overflow = originalOverflow;
        document.removeEventListener('wheel', handleWheel);
      };
    }
  }, [show]);

  // 使用 useCallback 稳定函数引用
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // PC 端弹窗内容
  const PCContent = useCallback(
    () => (
      <div
        ref={pcPopupRef}
        className="fixed right-0 bottom-0 z-[3000] touch-none w-[722px] h-[639px] rounded-t-[12px] transition-transform duration-300 ease-in-out"
        style={{ transform: show ? 'translateY(0)' : 'translateY(105%)' }}
      >
        {/* 头部 */}
        <div className="h-[50px] leading-[50px] text-[16px] text-white bg-primary-purple relative text-center font-bold rounded-t-[12px]">
          {t('serviceExperienceConsultant')}
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: '20px',
              top: '50%',
              transform: 'translateY(-50%)',
            }}
            size="small"
          >
            <CloseOutlined className="text-[24px] text-white" />
          </IconButton>
        </div>

        {/* iframe 容器 */}
        <div className="w-full h-[589px] bg-white relative">
          <InstadeskIframe url={url} loading={loading} height="589" className="border-0 bg-white" />
        </div>
      </div>
    ),
    [show, url, loading],
  );

  return (
    <>
      {/* PC 端 */}
      <WebOnly>
        <PCContent />
      </WebOnly>

      {/* 移动端，不能用useCallback包裹，会导致重复popup */}
      <WapOnly>
        <Popup
          open={show}
          position="bottom"
          height="100%"
          onClose={handleClose}
          hideCloseIcon
          // overlay={false}
          bodyClassName="!p-0 !max-h-[100vh]"
          round={false}
        >
          {/* 头部 */}
          <div className="h-[50px] leading-[50px] text-base text-white bg-primary-purple relative text-center font-bold">
            {t('serviceExperienceConsultant')}
            <IconButton
              onClick={handleClose}
              sx={{
                position: 'absolute',
                right: '5.333vw',
                top: '50%',
                transform: 'translateY(-50%)',
              }}
              size="small"
            >
              <CloseOutlined className="text-[24px] text-white" />
            </IconButton>
          </div>

          {/* iframe 容器 */}
          <div className="h-[calc(100%-55px)] relative">
            <InstadeskIframe url={url} loading={loading} height="100%" className="border-0" />
          </div>
        </Popup>
      </WapOnly>
    </>
  );
}
